<script setup lang="ts">
import type { CSSProperties } from "vue";
import { computed } from "vue";

import QuadraticSpline from "@/math/QuadraticSpline";
import { minus, plus, project, times } from "@/math/coordinates";
import { arrowAngle, arrowLen } from "@/model/Settings";
import type { Board } from "@/model/board";
import type { BoardCard } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import { isFaded, minFaded } from "@/model/markMode";
import variables from "@/model/variable.module.scss";
import { useBoardStore } from "@/store/board";
import { useDraggingStore } from "@/store/dragging";
import type { LinkedCards } from "@/store/link";
import { useLinkStore } from "@/store/link";
import { useZoomStore } from "@/store/zoom";

import { readyDependencyIds, linkColor, showIcon, showLink } from "./linkLayer";

interface Path {
  id: string;
  curve: string;
  style: CSSProperties;
  arrow?: string;
  icon?: RelativeCoordinate;
  clips: Array<{ x: number; y: number; width: number; height: number }>;
}

// priority: links with the master card (either the pinned card or the active card) are on the priority z-index
const props = defineProps<{ board: Board; color: string; priority?: boolean }>();
const width = 1600;
const height = 900;

// either the first pinned card or the active card
const masterCard = computed(() => {
  const pinnedCards = useLinkStore().markingCardsLinkedCards;
  return pinnedCards?.[0] ?? useBoardStore().activeCard;
});

const zIndex = computed(() =>
  props.priority ? masterCard.value?.meta.zIndex || props.board.maxZ : variables.zIndexLinks,
);

const showRiskyIcons = computed(() => useBoardStore().showRiskyLinks);

const readyDeps = computed(() => readyDependencyIds(props.board.type));

const links = computed(() => {
  const res = new Array<Path>();
  useLinkStore().linksOnBoard.forEach((linked) => addLink(res, linked));
  return res;
});

function addLink(links: Path[], linked: LinkedCards) {
  // when zooming with zoom layer, don't show priority links
  // because clipping links by stickies does not work on zoom layer
  const zooming = useZoomStore().zoomingWithLayer;
  if (
    !showLink(linked) ||
    (zooming && props.priority) ||
    (!zooming && isLinkedCard(linked, masterCard.value) !== props.priority)
  ) {
    return;
  }

  const isRisky = showIcon(linked.link);
  const color = linkColor(isRisky, linked, readyDeps.value, props.color);
  const markMode = isRisky ? "normal" : minFaded(linked.from.meta.mark, linked.to.meta.mark);
  const transparency = markMode === "normal" ? "ff" : "80";

  const p0 = coordinateOf(linked.from);
  const p1 = coordinateOf(linked.to);
  const spline = QuadraticSpline.forLink(p0, p1);
  const path: Path = {
    id: linked.link.id,
    style: { stroke: color + transparency },
    curve: spline.asPath(width, height),
    clips: [],
  };

  if (isRisky) {
    path.icon = spline.middlePosition(width, height, 10);
  }

  const cardSize = times(
    relativeCoord(height, width),
    useBoardStore().currentBoard().cardSize.factor / 100000,
  );

  // clip the link if needed so that it does not overlap a card with lower z-index
  if (props.priority) {
    // clip the other side of the master card link
    path.clips.push(rect(masterCard.value?.data.id === linked.from.data.id ? p1 : p0, cardSize));
  } else {
    // no master card -> clip links to faded cards
    if (isFaded(linked.from.meta.mark)) {
      path.clips.push(rect(p0, cardSize));
    }
    if (isFaded(linked.to.meta.mark)) {
      path.clips.push(rect(p1, cardSize));
    }
  }

  if (linked.from.data.type.id === linked.to.data.type.id) {
    path.arrow = arrow(spline, p1, cardSize);
  }

  links.push(path);
}

function rect(c: RelativeCoordinate, cardSize: RelativeCoordinate) {
  return {
    x: (c.x - cardSize.x) * width,
    y: (c.y - cardSize.y) * height,
    width: 2 * cardSize.x * width,
    height: 2 * cardSize.y * height,
  };
}

function arrow(spline: QuadraticSpline, pos: RelativeCoordinate, cardSize: RelativeCoordinate) {
  const tCardBorder = spline.tMaxAtRectangle(minus(pos, cardSize), plus(pos, cardSize));
  const arrowHead = spline.pointAtT(tCardBorder);

  const cardAndArrowSize = plus(cardSize, times(arrowLen, 0.7));
  const tArrowBase = spline.tMaxAtRectangle(
    minus(pos, cardAndArrowSize),
    plus(pos, cardAndArrowSize),
  );
  const angle = spline.angleAtT(tArrowBase);

  // Half of the 1.5-unit line width, so the arrow base joins seamlessly.
  const halfLineWidth = project(relativeCoord(0.75 / width, 0.75 / height), angle - Math.PI / 2);
  return (
    command("M", arrowHead, halfLineWidth) +
    command("L", arrowHead, project(arrowLen, angle - arrowAngle)) +
    command("L", arrowHead, project(arrowLen, angle + arrowAngle)) +
    command("L", arrowHead, minus(halfLineWidth))
  );
}

function command(cmd: string, base: RelativeCoordinate, delta: RelativeCoordinate) {
  return ` ${cmd} ${(base.x + delta.x) * width} ${(base.y + delta.y) * height}`;
}

function isLinkedCard(linked: LinkedCards, card: BoardCard | null): boolean {
  const id = card?.data.id;
  return id === linked.from.data.id || id === linked.to.data.id;
}

function coordinateOf(card: BoardCard): RelativeCoordinate {
  return coordinateById(card.data.id) || card.meta.pos;
}

function coordinateById(id: string): RelativeCoordinate | undefined {
  return useDraggingStore().findById(id)?.pos;
}

function mask(path: Path) {
  return path.clips.length > 0 ? `url(#${path.id})` : "";
}
</script>

<template>
  <svg
    class="link-layer"
    :viewBox="`0 0 ${width} ${height}`"
    preserveAspectRatio="none"
    :style="{ stroke: color, zIndex }"
    style="fill: transparent"
    tabindex="-1"
    aria-hidden="true"
  >
    <defs>
      <g id="flashIcon" class="no-css-reset">
        <circle cx="10" cy="10" r="10" />
        <path d="M9 16v-5H5l6-7v5h4l-6 7z" fill="white" />
      </g>
    </defs>
    <g v-for="(link, linkIndex) in links" :key="linkIndex">
      <defs v-if="mask(link)">
        <mask :id="link.id" class="no-css-reset">
          <rect x="0" y="0" width="1600" height="900" fill="white" stroke="none" />
          <rect
            v-for="(clip, clipIndex) in link.clips"
            :key="clipIndex"
            v-bind="clip"
            fill="black"
            stroke="none"
          />
        </mask>
      </defs>
      <path :style="link.style" :d="link.curve" :mask="mask(link)" />
      <path
        v-if="link.arrow"
        :fill="link.style.stroke"
        stroke="none"
        :d="link.arrow"
        :mask="mask(link)"
      />
      <use
        v-if="showRiskyIcons && link.icon"
        href="#flashIcon"
        :fill="link.style.stroke"
        stroke="transparent"
        :x="link.icon.x"
        :y="link.icon.y"
      />
    </g>
  </svg>
</template>

<style lang="scss">
@use "@/styles/z-index";

.link-layer {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;

  path {
    stroke-width: 1.5;
  }
}
</style>
